import type { TFunction } from 'i18next';
import { z } from 'zod';

import { createValidationMessages } from '@/shared/libs';
import {
  countriesInRegions,
  draftToOfferInput,
  draftToOfferUpdate,
  duplicateToDraft,
  emptyOfferDraft,
  type OfferDraft,
  offerToDraft,
  regionLabelToCode,
} from '@entities/Offer';
import type {
  Offer,
  OfferDuplicateDraft,
  OfferInput,
} from '@psp-aggregator/sdk';

const legSchema = z.object({
  asset: z.string(),
  cost_fixed: z.string(),
  cost_pct: z.string(),
  enabled: z.boolean(),
  kyc_limits: z.string(),
  limits: z.string(),
  methods: z.array(z.string()),
  min_amount: z.string(),
  min_fee_fixed: z.string(),
  min_fee_pct: z.string(),
  provider: z.string(),
  rate: z.string(),
  sell_fixed: z.string(),
  sell_pct: z.string(),
  terms: z.string(),
});

export const OFFER_TRAFFIC_TYPES = [
  'Trusted',
  'FTD',
  'NFTD',
  'Incentive',
  'Grey',
  'Quasi-ecom',
] as const;

export type OfferFormStepKey =
  | 'pages.admin.offers.steps.alias'
  | 'pages.admin.offers.steps.baseCurrency'
  | 'pages.admin.offers.steps.coverage'
  | 'pages.admin.offers.steps.cryptoRate'
  | 'pages.admin.offers.steps.currencies'
  | 'pages.admin.offers.steps.exceptions'
  | 'pages.admin.offers.steps.integration'
  | 'pages.admin.offers.steps.legs'
  | 'pages.admin.offers.steps.name'
  | 'pages.admin.offers.steps.provider'
  | 'pages.admin.offers.steps.settlementRate'
  | 'pages.admin.offers.steps.traffic';

const filled = (value: string): boolean => value.trim() !== '';

const legReady = (
  leg: OfferDraft['legs']['payin'],
  settlement: boolean,
): boolean => {
  if (!leg.enabled) {
    return true;
  }

  const descriptor = settlement
    ? filled(leg.terms) || leg.methods.length > 0
    : leg.methods.length > 0;
  const cost = filled(leg.cost_pct) || filled(leg.cost_fixed);
  const sell = filled(leg.sell_pct) || filled(leg.sell_fixed);

  return descriptor && cost && sell;
};

export const offerFormStep = (
  values: OfferDraft,
): OfferFormStepKey | undefined => {
  if (!values.provider_id) {
    return 'pages.admin.offers.steps.provider';
  }

  if (!values.name.trim()) {
    return 'pages.admin.offers.steps.name';
  }

  if (!values.alias.trim()) {
    return 'pages.admin.offers.steps.alias';
  }

  if (!values.base_currency) {
    return 'pages.admin.offers.steps.baseCurrency';
  }

  if (values.regions.length === 0 && values.countries.length === 0) {
    return 'pages.admin.offers.steps.coverage';
  }

  if (values.currencies.length === 0) {
    return 'pages.admin.offers.steps.currencies';
  }

  if (!values.traffic_type) {
    return 'pages.admin.offers.steps.traffic';
  }

  if (values.integration_methods.length === 0) {
    return 'pages.admin.offers.steps.integration';
  }

  const enabled = (
    ['payin', 'payout', 'settlement', 'crypto_topup'] as const
  ).filter((leg) => values.legs[leg].enabled);

  if (
    enabled.length === 0 ||
    !legReady(values.legs.payin, false) ||
    !legReady(values.legs.payout, false) ||
    !legReady(values.legs.settlement, true) ||
    !legReady(values.legs.crypto_topup, true)
  ) {
    return 'pages.admin.offers.steps.legs';
  }

  if (values.legs.settlement.enabled && !values.legs.settlement.rate.trim()) {
    return 'pages.admin.offers.steps.settlementRate';
  }

  if (
    values.legs.crypto_topup.enabled &&
    !values.legs.crypto_topup.rate.trim()
  ) {
    return 'pages.admin.offers.steps.cryptoRate';
  }

  const covered = new Set(countriesInRegions(values.regions));
  const outside = values.restricted_countries.filter(
    (country) => !covered.has(country.toUpperCase()),
  );
  const global = values.regions.some(
    (region) => regionLabelToCode(region) === 'GLOBAL',
  );

  if (!global && values.regions.length > 0 && outside.length > 0) {
    return 'pages.admin.offers.steps.exceptions';
  }

  return undefined;
};

export const offerFormProgress = (values: OfferDraft): number => {
  const enabled = (
    ['payin', 'payout', 'settlement', 'crypto_topup'] as const
  ).filter((leg) => values.legs[leg].enabled);
  const legsReady =
    enabled.length > 0 &&
    legReady(values.legs.payin, false) &&
    legReady(values.legs.payout, false) &&
    legReady(values.legs.settlement, true) &&
    legReady(values.legs.crypto_topup, true);
  const covered = new Set(countriesInRegions(values.regions));
  const global = values.regions.some(
    (region) => regionLabelToCode(region) === 'GLOBAL',
  );
  const exceptionsReady =
    global ||
    values.regions.length === 0 ||
    values.restricted_countries.every((country) =>
      covered.has(country.toUpperCase()),
    );
  const steps = [
    Boolean(values.provider_id),
    Boolean(values.name.trim()),
    Boolean(values.alias.trim()),
    Boolean(values.base_currency),
    values.regions.length > 0 || values.countries.length > 0,
    values.currencies.length > 0,
    Boolean(values.traffic_type.trim()),
    values.integration_methods.length > 0,
  ];

  if (values.base_currency) {
    steps.push(legsReady);
  }

  if (values.legs.settlement.enabled) {
    steps.push(Boolean(values.legs.settlement.rate.trim()));
  }

  if (values.legs.crypto_topup.enabled) {
    steps.push(Boolean(values.legs.crypto_topup.rate.trim()));
  }

  if (values.restricted_countries.length > 0) {
    steps.push(exceptionsReady);
  }

  const done = steps.filter(Boolean).length;

  return steps.length === 0 ? 0 : Math.round((done / steps.length) * 100);
};

export const createOfferFormSchema = (t: TFunction) => {
  const messages = createValidationMessages(t);

  return z
    .object({
      alias: z.string().min(1, { message: messages.required() }),
      base_currency: z.string(),
      chargeback: z.string(),
      countries: z.array(z.string()),
      currencies: z.array(z.string()),
      custom_fields: z.array(z.object({ key: z.string(), value: z.string() })),
      details: z.string(),
      flow: z.string(),
      guarantee_amount: z.string(),
      integration_methods: z.array(z.string()),
      legs: z.object({
        crypto_topup: legSchema,
        payin: legSchema,
        payout: legSchema,
        settlement: legSchema,
      }),
      name: z.string().min(1, { message: messages.required() }),
      provider_id: z.string().min(1, { message: messages.required() }),
      refund: z.string(),
      regions: z.array(z.string()),
      requirements: z.string(),
      restricted_countries: z.array(z.string()),
      rolling_reserve: z.object({
        cap_amount: z.string(),
        hold_days: z.string(),
        reserve_pct: z.string(),
      }),
      traffic_type: z.string(),
    })
    .superRefine((values, context) => {
      if (offerFormStep(values)) {
        context.addIssue({
          code: 'custom',
          message: t('validation.offerLegsRequired'),
          path: ['legs'],
        });
      }
    });
};

export type OfferFormValues = OfferDraft;

export const OFFER_FORM_DEFAULT_VALUES: OfferFormValues = emptyOfferDraft();

export const offerToFormValues = (offer: Offer): OfferFormValues =>
  offerToDraft(offer);

export const duplicateToFormValues = (
  draft: OfferDuplicateDraft,
): OfferFormValues => duplicateToDraft(draft);

export const formValuesToOfferInput = (values: OfferFormValues): OfferInput =>
  draftToOfferInput(values);

export const formValuesToOfferUpdate = (
  values: OfferFormValues,
): ReturnType<typeof draftToOfferUpdate> => draftToOfferUpdate(values);
